import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container } from "react-bootstrap";
import List from "./List";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserSubscriptions, fetchSubscriptionStats, fetchPlans } from "../../../redux/slice/subscriptionSlice";
import { useLoader } from "../../../context/LoaderContext";
import { TabCard } from "../../../components/custom-card/Cards";
import { Users, UserCheck, UserMinus, UserX, CircleDollarSign, Landmark, Ban } from "lucide-react";

const Subscriptions = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();

  // Pagination
  const [limit, setLimit] = useState({ value: 10, label: 10 });
  const [currentPage, setCurrentPage] = useState(1);

  // Search Bar
  const [searchTerm, setSearchTerm] = useState("");

  // Advanced Multi-Selection Filters State
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);

  const { userSubscriptions = [], totalUserSubscriptions = 0, stats = null, plans = [] } = useSelector(
    (state) => state?.subscription || {}
  );

  const searchTimeout = useRef(null);

  // Fetch plans on mount
  useEffect(() => {
    dispatch(fetchPlans({ limit: 100 }));
  }, [dispatch]);

  const getAllSubscriptionsData = useCallback(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    setLoading(true);
    searchTimeout.current = setTimeout(() => {
      Promise.all([
        dispatch(fetchSubscriptionStats()).unwrap(),
        dispatch(
          fetchUserSubscriptions({
            page: currentPage,
            limit: limit?.value,
            user_id: searchTerm,
            status: selectedStatuses.map(s => s.value).join(','),
            source: selectedSources.map(s => s.value).join(','),
            plan_ids: selectedPlans.map(p => p.value).join(','),
          })
        ).unwrap()
      ]).finally(() => {
        setLoading(false);
      });
    }, 500);
  }, [currentPage, limit?.value, searchTerm, selectedStatuses, selectedSources, selectedPlans, dispatch, setLoading]);

  useEffect(() => {
    getAllSubscriptionsData();
    return () => clearTimeout(searchTimeout.current);
  }, [getAllSubscriptionsData]);

  const AllTypesOfUserCard = [
    {
      id: 1,
      total: stats?.totalSubscribers ?? 0,
      title: "Total Subscriptions",
      icon: <Users size={24} />,
      iconClassName: "icon-primary",
      statusValue: "all"
    },
    {
      id: 2,
      total: stats?.activeSubscribers ?? 0,
      title: "Active Subscriptions",
      icon: <UserCheck size={24} />,
      iconClassName: "icon-success",
      statusValue: "active"
    },
    {
      id: 3,
      total: stats?.expiredSubscribers ?? 0,
      title: "Expired Subscriptions",
      icon: <UserMinus size={24} />,
      iconClassName: "icon-warning",
      statusValue: "expired"
    },
    {
      id: 4,
      total: stats?.cancelledSubscribers ?? 0,
      title: "Cancelled Subscriptions",
      icon: <UserX size={24} />,
      iconClassName: "icon-secondary",
      statusValue: "cancelled"
    },
    {
      id: 5,
      total: stats?.revokedSubscribers ?? 0,
      title: "Revoked Subscriptions",
      icon: <Ban size={24} />,
      iconClassName: "icon-danger",
      statusValue: "revoked"
    },
    {
      id: 8,
      total: stats?.failedSubscribers ?? 0,
      title: "Failed Subscriptions",
      icon: <UserX size={24} />,
      iconClassName: "icon-danger",
      statusValue: "failed"
    },
    {
      id: 6,
      total: stats?.monthlyRevenue ? `₹${parseFloat(stats.monthlyRevenue).toLocaleString()}` : "₹0",
      title: "Monthly Revenue",
      icon: <CircleDollarSign size={24} />,
      iconClassName: "icon-info",
      statusValue: null
    },
    {
      id: 7,
      total: stats?.totalRevenue ? `₹${parseFloat(stats.totalRevenue).toLocaleString()}` : "₹0",
      title: "Total Revenue",
      icon: <Landmark size={24} />,
      iconClassName: "icon-secondary",
      statusValue: null
    }
  ];

  return (
    <>
      <Container fluid className="px-0">
        {/* Card Section */}
        <div className="grid gap-4">
          {AllTypesOfUserCard?.map((item, i) => (
            <TabCard
              key={i}
              count={item?.total}
              title={item?.title}
              className={`g-col-4 g-col-xxl-3 ${!item.statusValue ? "pe-none" : "cursor-pointer"}`}
              icon={item?.icon}
              active={
                item.statusValue === "all"
                  ? selectedStatuses.length === 0
                  : selectedStatuses.length === 1 && selectedStatuses[0].value === item.statusValue
              }
              iconClassName={item?.iconClassName || "icon-secondary"}
              onClick={() => {
                if (item.statusValue) {
                  if (item.statusValue === "all") {
                    setSelectedStatuses([]);
                  } else {
                    setSelectedStatuses([{ value: item.statusValue, label: item.title.replace(" Subscriptions", "") }]);
                  }
                  setCurrentPage(1);
                }
              }}
            />
          ))}
        </div>

        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          {/* List Section */}
          <List
            data={userSubscriptions}
            searchTerm={searchTerm}
            setCurrentPage={setCurrentPage}
            setSearchTerm={setSearchTerm}
            currentPage={currentPage}
            limit={limit?.value}
            setLimit={setLimit}
            total={totalUserSubscriptions}
            refresh={getAllSubscriptionsData}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            selectedSources={selectedSources}
            setSelectedSources={setSelectedSources}
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
            plans={plans}
          />
        </div>
      </Container>
    </>
  );
};

export default Subscriptions;
